import { useCallback, useEffect, useRef, useState } from 'react';
import type { SourcingFilterOptions } from '../types';
import { normalizeSourcingFilterOptions } from './filters/utils/sourcingFilterModel';
import useRecruitmentPoolSourcingService from '../useRecruitmentPoolSourcingService';

type OptionsRequestState = {
  isActive: boolean;
  requestId: number;
};

const useRecruitmentPoolSourcingOptions = (recruitmentPoolId?: string) => {
  const { getFilterOptions } = useRecruitmentPoolSourcingService();
  const [filterOptions, setFilterOptions] = useState<SourcingFilterOptions>();
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const requestStateRef = useRef<OptionsRequestState>({ isActive: false, requestId: 0 });

  const loadFilterOptions = useCallback(async () => {
    const requestState = requestStateRef.current;
    const requestId = ++requestState.requestId;

    setLoading(true);
    setError(null);

    if (!recruitmentPoolId) {
      setError(new Error('Recruitment pool was not specified.'));
      setLoading(false);
      return;
    }

    try {
      const options = await getFilterOptions(recruitmentPoolId);
      if (!requestState.isActive || requestState.requestId !== requestId) return;

      setFilterOptions(normalizeSourcingFilterOptions(options));
    } catch (requestError: unknown) {
      if (requestState.isActive && requestState.requestId === requestId) {
        setError(requestError instanceof Error ? requestError : new Error('Unable to load sourcing options.'));
      }
    } finally {
      if (requestState.isActive && requestState.requestId === requestId) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recruitmentPoolId]);

  useEffect(() => {
    const requestState = { isActive: true, requestId: 0 };
    requestStateRef.current = requestState;

    setFilterOptions(undefined);
    setError(null);

    if (recruitmentPoolId) {
      void loadFilterOptions();
    } else {
      setError(new Error('Recruitment pool was not specified.'));
      setLoading(false);
    }

    return () => {
      requestState.isActive = false;
    };
  }, [loadFilterOptions, recruitmentPoolId]);
  return {
    filterOptions,
    isLoading,
    error,
    retry: loadFilterOptions,
  };
};

export default useRecruitmentPoolSourcingOptions;
